import React, { useEffect, useMemo, useRef, useState } from "react";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { SnGridRef, ColumnConfig } from "@shared/components/GridV2/SnGridTypes";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { menuInfo } from "@shared/type/CommonType";
import { Project, projectAgg, todayStr } from "./wbsCalc";

/**
 * 프로젝트목록 (ISS2026100053) — SnGrid 기반. WBS현황 간트와 같은 집계(wbsCalc).
 * 컬럼: 프로젝트 / PM / 계획기간 / 계획% / 실적% / 달성률 / 상태
 */
const period = (ps: string, pe: string) =>
  ps && pe
    ? `${ps.slice(0, 4)}.${ps.slice(4, 6)}.${ps.slice(6, 8)} ~ ${pe.slice(0, 4)}.${pe.slice(4, 6)}.${pe.slice(6, 8)}`
    : "-";

// 폴백 컬럼 (sys_formgridcolumn 미배포 시) — 메타형, SnColumnsCreate 가 colDef 로 변환
const base = { cd_firm: "", cd_menu: "SN_SFA_PJT_REG_002", cd_grid: "grid1", cd_user: "", fg_visible: "Y", yn_allowsorting: "Y" };
const FALLBACK_COLUMNS: ColumnConfig[] = [
  { ...base, cd_column: "nm_pjt", nm_column: "프로젝트", tp_column: "SnTextBox", qt_width: 220, no_seq: 1 },
  { ...base, cd_column: "pm", nm_column: "PM", tp_column: "SnTextBox", qt_width: 100, no_seq: 2 },
  { ...base, cd_column: "period", nm_column: "계획기간", tp_column: "SnTextBox", qt_width: 230, no_seq: 3 },
  { ...base, cd_column: "plan", nm_column: "계획%", tp_column: "SnNumberBox", qt_width: 90, no_seq: 4 },
  { ...base, cd_column: "act", nm_column: "실적%", tp_column: "SnNumberBox", qt_width: 90, no_seq: 5 },
  { ...base, cd_column: "achieve", nm_column: "달성률%", tp_column: "SnNumberBox", qt_width: 100, no_seq: 6 },
  { ...base, cd_column: "status", nm_column: "상태", tp_column: "SnTextBox", qt_width: 90, no_seq: 7 },
];

const ProjectList: React.FC<{
  projects: Project[];
  filterProjectId?: string;
  filterStatus?: string;
}> = ({ projects, filterProjectId, filterStatus }) => {
  const gridRef = useRef<SnGridRef>(null);
  const base = todayStr();

  // 컬럼: sys_formgridcolumn 메타 우선, 없으면 폴백
  const [cols, setCols] = useState<ColumnConfig[]>(FALLBACK_COLUMNS);
  useEffect(() => {
    (async () => {
      try {
        const rtn: any = await getInitFormData({
          menuInfo: { cd_menu: "SN_SFA_PJT_REG_002" } as menuInfo,
          etcInfo: {},
        });
        const g = rtn?.grid?.grid1;
        if (Array.isArray(g) && g.length) setCols(g);
      } catch {
        /* 메타 조회 실패 시 폴백 유지 */
      }
    })();
  }, []);

  const rowData = useMemo(
    () =>
      projects
        .filter((p) => p.acts.length)
        .map((p) => ({ p, agg: projectAgg(p, base) }))
        .filter(({ p, agg }) => {
          if (filterProjectId && p.id !== filterProjectId) return false;
          if (filterStatus && agg.status !== filterStatus) return false;
          return true;
        })
        .map(({ p, agg }) => ({
          id: p.id,
          nm_pjt: p.name,
          pm: p.pm,
          period: period(agg.ps, agg.pe),
          plan: agg.plan,
          act: agg.act,
          achieve: agg.achieve,
          status: agg.status,
        })),
    [projects, filterProjectId, filterStatus, base]
  );

  return (
    <div className="h-full">
      <SnGrid
        ref={gridRef}
        columnConfig={cols}
        rowData={rowData}
        etcProps={{ rowSelection: "single", showFooter: false }}
      />
    </div>
  );
};

export default ProjectList;
